import type { ReactNode } from 'react';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/lib/utils';

type DockWidth = 'md' | 'lg' | 'xl' | 'full';

const WIDTH_CLASS: Record<DockWidth, string> = {
  md: 'w-full sm:max-w-[1000px]',
  lg: 'w-full sm:max-w-[1000px]',
  xl: 'w-full sm:max-w-[1000px]',
  full: 'w-full sm:max-w-[1000px]',
};

interface RightDockDetailSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  summary?: ReactNode;
  footer?: ReactNode;
  width?: DockWidth;
  className?: string;
  closeOnOutsideInteraction?: boolean;
  closeOnEscape?: boolean;
  children: ReactNode;
}

export function RightDockDetailSheet({
  open,
  onOpenChange,
  title,
  description,
  summary,
  footer,
  width = 'xl',
  className,
  closeOnOutsideInteraction = true,
  closeOnEscape = true,
  children,
}: RightDockDetailSheetProps) {
  const accessibleDescription = description ?? `${title} details panel`;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className={cn('flex h-full min-w-0 flex-col overflow-hidden p-0', WIDTH_CLASS[width], className)}
        onPointerDownOutside={(event) => {
          if (!closeOnOutsideInteraction) event.preventDefault();
        }}
        onInteractOutside={(event) => {
          if (!closeOnOutsideInteraction) event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          if (!closeOnEscape) event.preventDefault();
        }}
      >
        <div className="shrink-0 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <SheetHeader className="px-6 pb-4 pt-6 pr-14">
            <SheetTitle>{title}</SheetTitle>
            <SheetDescription className={description ? undefined : 'sr-only'}>
              {accessibleDescription}
            </SheetDescription>
          </SheetHeader>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden">
          <div className="min-w-0 space-y-6 px-6 py-6">
            {summary ? <div>{summary}</div> : null}
            <div className="min-w-0">{children}</div>
          </div>
        </div>

        {footer ? (
          <div className="shrink-0 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
            <div className="min-w-0 px-6 py-4">{footer}</div>
          </div>
        ) : null}
      </SheetContent>
    </Sheet>
  );
}
